import React from 'react';
import { motion } from 'framer-motion';
import { Home, ArrowLeftRight, GitBranch, BarChart3, Coins, Eye } from 'lucide-react';
import { TabType } from '../../types';
import WalletButton from '../Wallet/WalletButton';
import NetworkSelector from '../NetworkSelector';

interface HeaderProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
}

const Header: React.FC<HeaderProps> = ({ activeTab, onTabChange }) => {
  const tabs = [
    { id: 'home' as TabType, label: 'Home', icon: Home },
    { id: 'swap' as TabType, label: 'Swap', icon: ArrowLeftRight },
    { id: 'bridge' as TabType, label: 'Bridge', icon: GitBranch },
    { id: 'polymarket' as TabType, label: 'Polymarket', icon: BarChart3 },
    { id: 'lend' as TabType, label: 'Lend', icon: Coins },
    { id: 'watcher' as TabType, label: 'Watcher', icon: Eye, comingSoon: true },
  ];

  return (
    <motion.header 
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="backdrop-blur-xl bg-black/20 border border-white/10 rounded-xl sm:rounded-2xl p-2 sm:p-4 mb-4 sm:mb-8 sticky top-2 sm:top-4 z-50"
    >
      {/* Mobile-first layout */}
      <div className="flex flex-col gap-2 sm:gap-4">
        {/* Top row: Logo on left, Wallet/Network on right */}
        <div className="flex items-center justify-between gap-2">
          {/* Logo and Brand - Left Side - Responsive */}
          <motion.div 
            className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0"
            whileHover={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            {/* Logo - Responsive size */}
            <div className="w-10 h-10 sm:w-14 sm:h-14 flex items-center justify-center">
              <img
                src="/eredium-logo.webp"
                alt="Eredium Logo"
                className="w-10 h-10 sm:w-14 sm:h-14"
              />
            </div>
            
            {/* Brand Name - Responsive text */}
            <div className="flex flex-col">
              <h1 className="text-xl sm:text-3xl font-bold tracking-tight" style={{
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: '#e2e8f0',
                textShadow: '0 0 20px rgba(59, 130, 246, 0.4), 0 2px 6px rgba(0, 0, 0, 0.6)'
              }}>
                Eredium
              </h1>
            </div>
          </motion.div>

          {/* Network Selector & Wallet - Top Right - Responsive */}
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <NetworkSelector />
            <WalletButton />
          </div>
        </div>

        {/* Horizontal Divider - Hidden on mobile */}
        <div className="hidden lg:block h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
        
        {/* Bottom row: Navigation */}
        <div className="flex items-center justify-center">
          {/* Desktop Navigation Tabs */}
          <nav className="hidden lg:flex items-center space-x-2">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isComingSoon = 'comingSoon' in tab && tab.comingSoon;
              return (
                <motion.button
                  key={tab.id}
                  onClick={() => onTabChange(tab.id)}
                  className={`flex items-center space-x-2 px-6 py-2.5 rounded-xl transition-all duration-300 relative ${
                    activeTab === tab.id 
                      ? isComingSoon 
                        ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 shadow-[0_4px_24px_rgba(139,92,246,0.2),0_0_40px_rgba(236,72,153,0.1)] border border-purple-400/40'
                        : 'bg-gradient-to-r from-blue-500/20 to-purple-500/20 shadow-[0_4px_24px_rgba(59,130,246,0.2),0_0_40px_rgba(139,92,246,0.1)] border border-blue-400/40' 
                      : isComingSoon
                        ? 'bg-gradient-to-r from-purple-500/5 to-pink-500/5 border border-purple-400/20 hover:border-purple-400/40 hover:from-purple-500/10 hover:to-pink-500/10'
                        : 'bg-gradient-to-r from-blue-500/5 to-purple-500/5 border border-blue-400/20 hover:border-blue-400/40 hover:bg-gradient-to-r hover:from-blue-500/10 hover:to-purple-500/10'
                  }`}
                  style={{
                    color: '#fff',
                    fontWeight: 600,
                    backdropFilter: 'blur(16px)'
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 400, damping: 10 }}
                >
                  <Icon size={18} className={isComingSoon ? 'text-purple-400' : ''} />
                  <span className="text-sm">{tab.label}</span>
                  {isComingSoon && (
                    <span className="absolute -top-1 -right-1 px-1.5 py-0.5 text-[9px] font-bold bg-gradient-to-r from-purple-500 to-pink-500 rounded-full text-white">
                      SOON
                    </span>
                  )}
                </motion.button>
              );
            })}
          </nav>

          {/* Mobile Navigation Dropdown - Full width on mobile */}
          <div className="flex lg:hidden w-full">
            <div className="w-full bg-gradient-to-r from-blue-500/15 to-purple-500/12 rounded-lg px-3 py-2 border border-blue-400/40" style={{ backdropFilter: 'blur(16px)' }}>
              <select
                value={activeTab}
                onChange={(e) => onTabChange(e.target.value as TabType)}
                className="w-full bg-transparent text-white text-sm outline-none cursor-pointer"
                style={{ fontWeight: 600 }}
              >
                {tabs.map((tab) => (
                  <option key={tab.id} value={tab.id} className="bg-gray-900">
                    {tab.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;
